import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Typography,
    LinearProgress,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Chip,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    InsertDriveFile as FileIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { uploadDataDictionary } from '../../services/api';

/**
 * FileUploader Component
 * 
 * Drag-and-drop file uploader with multi-file support for data dictionary Excel files.
 */
function FileUploader({ projectId, sourceSystem, onUploadSuccess }) {
    const [uploading, setUploading] = useState(false);
    const [uploadQueue, setUploadQueue] = useState([]); // Files being uploaded
    const [results, setResults] = useState([]); // Upload results

    const onDrop = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        // Validate file types
        const validFiles = [];
        const invalidFiles = [];

        acceptedFiles.forEach(file => {
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (['.xlsx', '.xls'].includes(fileExtension)) {
                validFiles.push(file);
            } else {
                invalidFiles.push({ file, error: 'Invalid file type' });
            }
        });

        // Add invalid files to results
        if (invalidFiles.length > 0) {
            setResults(prev => [
                ...prev,
                ...invalidFiles.map(({ file, error }) => ({
                    fileName: file.name,
                    success: false,
                    error,
                }))
            ]);
        }

        if (validFiles.length === 0) return;

        // Initialize upload queue
        setUploadQueue(validFiles.map(file => ({
            file,
            status: 'pending',
            progress: 0,
        })));
        setUploading(true);

        // Upload files sequentially
        for (let i = 0; i < validFiles.length; i++) {
            const file = validFiles[i];

            // Update status to uploading
            setUploadQueue(prev => prev.map((item, idx) =>
                idx === i ? { ...item, status: 'uploading', progress: 30 } : item
            ));

            try {
                const result = await uploadDataDictionary(projectId, sourceSystem, file);

                // Update to success
                setUploadQueue(prev => prev.map((item, idx) =>
                    idx === i ? { ...item, status: 'success', progress: 100 } : item
                ));

                setResults(prev => [...prev, {
                    fileName: file.name,
                    success: true,
                    message: result.file_info?.table_review_total
                        ? `${result.message} Review ${result.file_info.table_review_total} suggested table removal(s) before continuing.`
                        : result.message,
                    fileInfo: result.file_info,
                }]);

            } catch (err) {
                const errorMessage = err.response?.data?.detail || 'Upload failed';

                // Update to error
                setUploadQueue(prev => prev.map((item, idx) =>
                    idx === i ? { ...item, status: 'error', progress: 0 } : item
                ));

                setResults(prev => [...prev, {
                    fileName: file.name,
                    success: false,
                    error: errorMessage,
                }]);
            }
        }

        setUploading(false);
        setUploadQueue([]);

        // Notify parent of successful uploads
        if (onUploadSuccess) {
            onUploadSuccess();
        }
    }, [projectId, sourceSystem, onUploadSuccess]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        multiple: true, // Enable multi-file upload
        disabled: uploading,
    });

    const clearResults = () => {
        setResults([]);
    };

    const clearResult = (index) => {
        setResults(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <Box>
            {/* Results */}
            {results.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    {results.map((result, index) => (
                        <Alert
                            key={index}
                            severity={result.success ? 'success' : 'error'}
                            sx={{ mb: 1 }}
                            icon={result.success ? <SuccessIcon /> : <ErrorIcon />}
                            action={
                                <IconButton size="small" onClick={() => clearResult(index)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            }
                        >
                            <Typography variant="subtitle2" fontWeight={600}>
                                {result.fileName}
                            </Typography>
                            <Typography variant="body2">
                                {result.success ? result.message : result.error}
                            </Typography>
                        </Alert>
                    ))}
                </Box>
            )}

            {/* Upload Queue Progress */}
            {uploadQueue.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                        Uploading {uploadQueue.length} file(s)...
                    </Typography>
                    <List dense>
                        {uploadQueue.map((item, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <FileIcon fontSize="small" color={
                                        item.status === 'success' ? 'success' :
                                            item.status === 'error' ? 'error' : 'action'
                                    } />
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.file.name}
                                    secondary={
                                        item.status === 'uploading' && (
                                            <LinearProgress
                                                variant="indeterminate"
                                                sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                            />
                                        )
                                    }
                                />
                                <Chip
                                    size="small"
                                    label={item.status}
                                    color={
                                        item.status === 'success' ? 'success' :
                                            item.status === 'error' ? 'error' :
                                                item.status === 'uploading' ? 'primary' : 'default'
                                    }
                                    variant="outlined"
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            {/* Dropzone */}
            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragActive
                        ? 'primary.main'
                        : uploading
                            ? 'text.disabled'
                            : 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: uploading ? 'wait' : 'pointer',
                    bgcolor: isDragActive
                        ? 'rgba(0, 119, 200, 0.08)'
                        : 'background.paper',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': !uploading ? {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(0, 119, 200, 0.04)',
                    } : {},
                }}
            >
                <input {...getInputProps()} />

                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" gutterBottom fontWeight={500} color="text.primary">
                    {isDragActive
                        ? 'Drop the files here'
                        : 'Drag & drop data dictionary files here'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    or click to browse (multiple files supported)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    Supports: .xlsx, .xls files with Table, Column, and Relationship sheets
                </Typography>
            </Box>
        </Box>
    );
}

export default FileUploader;
